/**
 * TimeInput — standardised time field for all modals
 *
 * Text input with a <datalist> of 30-minute options and a clock icon.
 * On blur, partial input is formatted to "H:MM AM/PM".
 * fontSize: 16px prevents iOS auto-zoom.
 */
import React from 'react';
import { Clock } from 'lucide-react';
import { TIME_OPTIONS, formatTimeToAMPM } from '@/utils/dateTimeInputUtils';

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  className?: string;
  listId?: string;
}

export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  id = 'time-input',
  disabled = false,
  className = '',
  listId,
}) => {
  const dataListId = listId ?? `${id}-options`;

  return (
    <div
      className={`relative w-full ${className}`}
      style={{ height: 48, margin: 0, padding: 0 }}
    >
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => {
          const formatted = formatTimeToAMPM(e.target.value);
          if (formatted) onChange(formatted);
        }}
        disabled={disabled}
        list={dataListId}
        autoComplete="off"
        placeholder="H:MM AM"
        className="text-black border border-gray-300 bg-white text-sm w-full rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          height: 48,
          minHeight: 48,
          maxHeight: 48,
          margin: 0,
          padding: '0.5rem 2.5rem 0.5rem 0.75rem',
          boxSizing: 'border-box',
          fontSize: 16,   // prevents iOS auto-zoom
          lineHeight: 1.5,
        }}
      />
      <datalist id={dataListId}>
        {TIME_OPTIONS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      <Clock className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none z-10" />
    </div>
  );
};
